import React, {useState, useMemo} from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Tooltip from "@mui/material/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ImageIcon from "@mui/icons-material/Image";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import {Helmet} from "react-helmet";
import {useNavigate} from "react-router";
import {useNews} from "../firebase/useNews";
import {useCheckIfAdmin} from "../firebase/useCheckIfAdmin";
import {EditArticle} from "../news/EditArticle";
import {DeleteArticle} from "../news/DeleteArticle";
import {formatArticleDate} from "../news/NewsCard";
import {SanitizedHtml} from "../app/SanitizedHtml";
import {UnauthorizedAccess} from "../app/UnauthorizedAccess";

export const AdminNews = () => {
    const navigate = useNavigate();
    const isAdmin = useCheckIfAdmin();

    const [forceRefresh, setForceRefresh] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [editArticleId, setEditArticleId] = useState(null);
    const [createArticleOpen, setCreateArticleOpen] = useState(false);
    const [deleteArticleId, setDeleteArticleId] = useState(null);
    const [previewArticle, setPreviewArticle] = useState(null);

    const [statusFilter, setStatusFilter] = useState("all");

    const news = useNews({
        orderBy: "published",
        order: "desc",
        forceRefresh: forceRefresh,
    });

    const statusCounts = useMemo(() => {
        const all = news || [];
        const published = all.filter((a) => a.status !== "draft").length;
        const draft = all.filter((a) => a.status === "draft").length;
        return { total: all.length, published, draft };
    }, [news]);

    const filteredNews = useMemo(() => {
        if (!news) return [];
        let list = news;
        if (statusFilter === "published") {
            list = list.filter((art) => art.status !== "draft");
        } else if (statusFilter === "draft") {
            list = list.filter((art) => art.status === "draft");
        }
        if (!searchQuery.trim()) return list;
        const q = searchQuery.toLowerCase().trim();
        return list.filter(
            (art) =>
                (art.title && art.title.toLowerCase().includes(q)) ||
                (art.text && art.text.toLowerCase().includes(q))
        );
    }, [news, searchQuery, statusFilter]);

    const articlesWithMediaCount = useMemo(() => {
        return (news || []).filter((art) => Boolean(art.media)).length;
    }, [news]);

    if (!isAdmin) {
        return <UnauthorizedAccess requiredRole="administrator" />;
    }

    return (
        <Box sx={{bgcolor: "#FAF8F5", minHeight: "85vh", py: {xs: 3, md: 5}}}>
            <Helmet>
                <title>Nyhetsadministrasjon | Kurs i bekymringsmestring</title>
            </Helmet>

            <Container maxWidth="lg" sx={{px: {xs: 2, sm: 3}}}>
                {/* Header Back & Action Bar */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/me")}
                        sx={{
                            color: "text.secondary",
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: 2,
                            minHeight: 44,
                            "&:hover": {color: "primary.main"},
                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                        }}
                    >
                        Tilbake til Min side
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={() => setCreateArticleOpen(true)}
                        sx={{
                            borderRadius: 2.5,
                            fontWeight: 700,
                            px: 3,
                            py: 1,
                            minHeight: 44,
                            textTransform: "none",
                            boxShadow: "0 4px 14px rgba(40, 82, 56, 0.2)",
                            "&:focus-visible": {outline: "2px solid #285238", outlineOffset: 2},
                        }}
                    >
                        Opprett ny artikkel
                    </Button>
                </Box>

                {/* Page Title & Stats */}
                <Box sx={{mb: 4}}>
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{
                            fontWeight: 800,
                            color: "primary.main",
                            fontSize: {xs: "1.75rem", sm: "2.25rem"},
                            letterSpacing: "-0.01em",
                            mb: 1,
                        }}
                    >
                        Nyhetsadministrasjon
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{mb: 2.5}}>
                        Administrer publiserte artikler, oppdater kurskunngjøringer og berik innholdet med bilder og formatering.
                    </Typography>

                    <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                        <Chip
                            label={`Totalt ${news?.length || 0} artikler`}
                            sx={{bgcolor: "rgba(40, 82, 56, 0.08)", color: "primary.main", fontWeight: 700}}
                        />
                        <Chip
                            icon={<ImageIcon sx={{fontSize: 16}} />}
                            label={`${articlesWithMediaCount} med bilde`}
                            variant="outlined"
                            sx={{borderColor: "rgba(40, 82, 56, 0.2)", color: "primary.main", fontWeight: 600}}
                        />
                    </Stack>
                </Box>

                {/* Search Bar */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                        bgcolor: "background.paper",
                        p: 1.5,
                        borderRadius: 3,
                        border: "1px solid rgba(40, 82, 56, 0.12)",
                    }}
                >
                    <TextField
                        placeholder="Søk i nyhetstittel eller innhold..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        fullWidth
                        inputProps={{
                            "aria-label": "Søk i nyhetstittel eller innhold",
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{color: "text.secondary"}} />
                                </InputAdornment>
                            ),
                            endAdornment: searchQuery ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="Tøm søk"
                                        size="small"
                                        onClick={() => setSearchQuery("")}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{"& .MuiOutlinedInput-root": {borderRadius: 2}}}
                    />
                </Box>

                {/* Status Filter Chips */}
                <Stack direction="row" spacing={1} sx={{mb: 3, flexWrap: "wrap", gap: 1}}>
                    <Chip
                        label={`Alle (${statusCounts.total})`}
                        color={statusFilter === "all" ? "primary" : "default"}
                        variant={statusFilter === "all" ? "filled" : "outlined"}
                        onClick={() => setStatusFilter("all")}
                        clickable
                        sx={{fontWeight: 700}}
                    />
                    <Chip
                        label={`Publiserte (${statusCounts.published})`}
                        color={statusFilter === "published" ? "success" : "default"}
                        variant={statusFilter === "published" ? "filled" : "outlined"}
                        onClick={() => setStatusFilter("published")}
                        clickable
                        sx={{fontWeight: 700}}
                    />
                    <Chip
                        label={`Utkast (${statusCounts.draft})`}
                        color={statusFilter === "draft" ? "warning" : "default"}
                        variant={statusFilter === "draft" ? "filled" : "outlined"}
                        onClick={() => setStatusFilter("draft")}
                        clickable
                        sx={{fontWeight: 700}}
                    />
                </Stack>

                {/* Articles List */}
                <Stack spacing={2}>
                    {filteredNews.length > 0 ? (
                        filteredNews.map((article) => (
                            <Card
                                key={article.id}
                                elevation={0}
                                sx={{
                                    border: "1px solid rgba(40, 82, 56, 0.12)",
                                    borderRadius: 3,
                                    transition: "all 0.2s ease",
                                    "&:hover": {
                                        boxShadow: "0 6px 20px rgba(0, 0, 0, 0.06)",
                                        borderColor: "primary.main",
                                    },
                                }}
                            >
                                <CardContent sx={{p: {xs: 2, sm: 2.5}}}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: {xs: "column", sm: "row"},
                                            alignItems: {xs: "flex-start", sm: "center"},
                                            justifyContent: "space-between",
                                            gap: 2,
                                        }}
                                    >
                                        {/* Article info */}
                                        <Box sx={{minWidth: 0, flex: 1}}>
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{mb: 0.75, flexWrap: "wrap", gap: 0.75}}>
                                                <Chip
                                                    label={article.status === "draft" ? "Utkast" : "Publisert"}
                                                    size="small"
                                                    color={article.status === "draft" ? "warning" : "success"}
                                                    variant={article.status === "draft" ? "filled" : "outlined"}
                                                    sx={{height: 20, fontSize: "0.7rem", fontWeight: 700}}
                                                />
                                                <CalendarTodayIcon sx={{fontSize: 14, color: "text.secondary"}} />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        color: "text.secondary",
                                                        fontWeight: 600,
                                                        textTransform: "capitalize",
                                                    }}
                                                >
                                                    {formatArticleDate(article.published) || "Ingen dato"}
                                                </Typography>
                                                {article.media && (
                                                    <Chip
                                                        icon={<ImageIcon sx={{fontSize: 12}} />}
                                                        label="Bilde"
                                                        size="small"
                                                        sx={{height: 18, fontSize: "0.65rem", fontWeight: 700}}
                                                    />
                                                )}
                                            </Stack>

                                            <Typography
                                                variant="h6"
                                                component="h2"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: "text.primary",
                                                    fontSize: {xs: "1.05rem", sm: "1.15rem"},
                                                    mb: 0.5,
                                                }}
                                            >
                                                {article.title}
                                            </Typography>

                                            <Box
                                                sx={{
                                                    color: "text.secondary",
                                                    fontSize: "0.875rem",
                                                    lineHeight: 1.5,
                                                    maxHeight: 48,
                                                    overflow: "hidden",
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                }}
                                            >
                                                <SanitizedHtml html={article.text || ""} />
                                            </Box>
                                        </Box>

                                        {/* Actions */}
                                        <Stack direction="row" spacing={1} sx={{alignSelf: {xs: "flex-end", sm: "center"}}}>
                                            <Tooltip title="Forhåndsvis artikkel">
                                                <IconButton
                                                    aria-label={`Forhåndsvis ${article.title}`}
                                                    onClick={() => setPreviewArticle(article)}
                                                    size="small"
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        border: "1px solid rgba(40, 82, 56, 0.2)",
                                                        borderRadius: 2,
                                                        color: "primary.main",
                                                        "&:hover": {bgcolor: "rgba(40, 82, 56, 0.08)"},
                                                        "&:focus-visible": {
                                                            outline: "2px solid #285238",
                                                            outlineOffset: 2,
                                                        },
                                                    }}
                                                >
                                                    <VisibilityIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Rediger artikkel">
                                                <IconButton
                                                    aria-label={`Rediger ${article.title}`}
                                                    onClick={() => setEditArticleId(article.id)}
                                                    size="small"
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        border: "1px solid rgba(40, 82, 56, 0.2)",
                                                        borderRadius: 2,
                                                        color: "primary.main",
                                                        "&:hover": {bgcolor: "rgba(40, 82, 56, 0.08)"},
                                                        "&:focus-visible": {
                                                            outline: "2px solid #285238",
                                                            outlineOffset: 2,
                                                        },
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="Slett artikkel">
                                                <IconButton
                                                    aria-label={`Slett ${article.title}`}
                                                    onClick={() => setDeleteArticleId(article.id)}
                                                    size="small"
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        border: "1px solid rgba(211, 47, 47, 0.2)",
                                                        borderRadius: 2,
                                                        color: "error.main",
                                                        "&:hover": {bgcolor: "rgba(211, 47, 47, 0.08)"},
                                                        "&:focus-visible": {
                                                            outline: "2px solid #d32f2f",
                                                            outlineOffset: 2,
                                                        },
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))
                    ) : (
                        <Box
                            sx={{
                                p: 5,
                                textAlign: "center",
                                bgcolor: "background.paper",
                                borderRadius: 3,
                                border: "1px dashed rgba(40, 82, 56, 0.2)",
                            }}
                        >
                            <Typography variant="body1" color="text.secondary">
                                {searchQuery
                                    ? `Ingen artikler matcher søket «${searchQuery}».`
                                    : "Det er ingen publiserte nyhetsartikler ennå. Klikk på «Opprett ny artikkel» for å lage en."}
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </Container>

            {/* Edit Article Dialog */}
            <EditArticle
                open={Boolean(editArticleId)}
                articleId={editArticleId}
                handleClose={(refresh) => {
                    if (refresh) setForceRefresh((fr) => fr + 1);
                    setEditArticleId(null);
                }}
            />

            {/* Create Article Dialog */}
            <EditArticle
                open={createArticleOpen}
                articleId={null}
                handleClose={(refresh) => {
                    if (refresh) setForceRefresh((fr) => fr + 1);
                    setCreateArticleOpen(false);
                }}
            />

            {/* Delete Article Dialog */}
            <DeleteArticle
                open={Boolean(deleteArticleId)}
                articleId={deleteArticleId}
                handleClose={(refresh) => {
                    if (refresh) setForceRefresh((fr) => fr + 1);
                    setDeleteArticleId(null);
                }}
            />

            {/* Live Preview Modal */}
            <Dialog
                open={Boolean(previewArticle)}
                onClose={() => setPreviewArticle(null)}
                maxWidth="md"
                fullWidth
                aria-labelledby="admin-preview-dialog-title"
                PaperProps={{sx: {borderRadius: 3, p: {xs: 1, sm: 2}, overscrollBehavior: "contain"}}}
            >
                {previewArticle && (
                    <>
                        <DialogTitle id="admin-preview-dialog-title" sx={{pr: 6, pt: 2}}>
                            <Chip
                                label={formatArticleDate(previewArticle.published) || "I dag"}
                                size="small"
                                sx={{
                                    mb: 1,
                                    bgcolor: "rgba(40, 82, 56, 0.08)",
                                    color: "primary.main",
                                    fontWeight: 600,
                                }}
                            />
                            <Typography variant="h5" component="div" sx={{fontWeight: 800, color: "primary.main"}}>
                                {previewArticle.title}
                            </Typography>
                            <IconButton
                                aria-label="Lukk forhåndsvisning"
                                onClick={() => setPreviewArticle(null)}
                                sx={{
                                    position: "absolute",
                                    right: 16,
                                    top: 16,
                                    color: "text.secondary",
                                    "&:focus-visible": {
                                        outline: "2px solid #285238",
                                        outlineOffset: 2,
                                    },
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{borderTop: "none", pt: 1, overscrollBehavior: "contain"}}>
                            {previewArticle.media && (
                                <Box
                                    component="img"
                                    src={previewArticle.media}
                                    alt={previewArticle.title}
                                    sx={{width: "100%", maxHeight: 380, objectFit: "cover", borderRadius: 2, mb: 3}}
                                />
                            )}
                            <Box
                                sx={{
                                    "& p": {lineHeight: 1.75, mb: 2, fontSize: "1.05rem", color: "text.primary"},
                                    "& h1, & h2, & h3": {color: "primary.main", mt: 2.5, mb: 1.5, fontWeight: 700},
                                    "& ul, & ol": {pl: 3, mb: 2},
                                    "& li": {mb: 0.5, lineHeight: 1.7},
                                    "& a": {
                                        color: "primary.main",
                                        textDecoration: "underline",
                                        fontWeight: 600,
                                        textUnderlineOffset: "3px",
                                        transition: "color 0.2s ease",
                                        "&:hover": {
                                            color: "primary.dark",
                                        },
                                        "&:focus-visible": {
                                            outline: "2px solid #285238",
                                            outlineOffset: "2px",
                                            borderRadius: "2px",
                                        },
                                    },
                                }}
                            >
                                <SanitizedHtml html={previewArticle.text || ""} />
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{px: 3, py: 2}}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setPreviewArticle(null)}
                                sx={{
                                    borderRadius: 2,
                                    fontWeight: 700,
                                    minHeight: 44,
                                    px: 3,
                                    "&:focus-visible": {
                                        outline: "2px solid #285238",
                                        outlineOffset: 2,
                                    },
                                }}
                            >
                                Lukk forhåndsvisning
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};
